/**
 * Pool Reports Summary Page
 *
 * Displays summary information for the selected pool report with
 * key metrics, quarterly/monthly breakdowns, and financial data
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Download, ChevronDown } from 'lucide-react';
import { Button, Alert, Skeleton } from '@/components/ui';
import { usePoolReportsStore } from '@/store';
import { useSummaryData } from '@/hooks/usePoolReports';
import { exportSummaryToCSV } from '@/utils/poolReportsCSV';
import { getVisibleHeadings, type HeadingProperty } from '@/config/headingProperties.config';
import type { Report, ReportType, ReportsHeading } from '@/types/poolReports.types';
import {
  poolReportTheadMutedClass,
  poolReportTheadStickyClass,
  poolReportThClass,
  poolReportThLabelClass,
} from '@/components/poolReports/poolReportTableChrome';

/** Shown as large KPI tiles above the compact detail grid */
const SUMMARY_PRIMARY_KEYS = new Set<keyof ReportsHeading>([
  'averagePoints',
  'averageEarnings',
  'oneHundredPointsEarnings',
]);

function splitHeadingProperties(props: HeadingProperty[]) {
  const primary = props.filter((p) => SUMMARY_PRIMARY_KEYS.has(p.key));
  const secondary = props.filter((p) => !SUMMARY_PRIMARY_KEYS.has(p.key));
  return { primary, secondary };
}

function summarySectionBadge(heading: ReportsHeading) {
  if (heading.isYTDValue) return 'Year-to-date';
  if (heading.isFullYearValue) return 'Full year';
  return 'Summary';
}

export function PoolReportsSummaryPage() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedReport, error, clearError } = usePoolReportsStore();

  const [summaryMobileLayout, setSummaryMobileLayout] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const apply = () => setSummaryMobileLayout(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  const effectiveReportType: ReportType =
    location.pathname.split('/')[2] === 'monthly' ? 'monthly' : 'yearly';

  const fileId = searchParams.get('fileId');
  const numericFileId = fileId ? parseInt(fileId, 10) : 0;
  const {
    headings: headingsData,
    details: detailsData,
    isLoading,
    isError,
    error: summaryError,
  } = useSummaryData(numericFileId, effectiveReportType, Boolean(fileId));

  const { headings, quarters, yearData } = useMemo(() => {
    const fetchedHeadings = headingsData || [];
    const fetchedDetails = detailsData || [];

    const months = fetchedDetails.filter((item) => item.sectionName.toLowerCase() === 'month');
    const quarterData = fetchedDetails.filter(
      (item) => item.sectionName.toLowerCase() === 'quarter'
    );
    const yearTotals = fetchedDetails.filter(
      (item) =>
        item.sectionName.toLowerCase() === 'year to date' ||
        item.sectionName.toLowerCase() === 'full year' ||
        item.sectionName.toLowerCase() === 'year' ||
        item.sectionName.toLowerCase() === 'ytd'
    );

    const quartersWithMonths = quarterData.map((quarter, index) => {
      const start = index === 0 ? 0 : index * 3;
      return {
        ...quarter,
        months: months.slice(start, start + 3),
      };
    });

    return {
      headings: fetchedHeadings,
      quarters: quartersWithMonths,
      yearData: yearTotals,
    };
  }, [headingsData, detailsData]);

  const loading = isLoading;
  const queryErrorMessage =
    isError && summaryError
      ? summaryError instanceof Error
        ? summaryError.message
        : 'Failed to load summary data'
      : null;

  const expandedParam = searchParams.get('expanded');
  const expandedQuarters = new Set(
    expandedParam ? expandedParam.split(',').map((i) => parseInt(i, 10)) : []
  );

  const toggleQuarter = (quarterIndex: number) => {
    const newExpanded = new Set(expandedQuarters);
    if (newExpanded.has(quarterIndex)) {
      newExpanded.delete(quarterIndex);
    } else {
      newExpanded.add(quarterIndex);
    }

    const newParams = new URLSearchParams(searchParams);
    if (newExpanded.size > 0) {
      newParams.set('expanded', Array.from(newExpanded).sort().join(','));
    } else {
      newParams.delete('expanded');
    }
    setSearchParams(newParams);
  };

  // Number formatter with thousand separators
  const formatNumber = (value: number | undefined, decimals = 0): string => {
    if (value === undefined || value === null) return '-';
    return value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  };

  // Enhanced CSV export using new utility
  const handleExportCSV = () => {
    if (headings.length === 0 && quarters.length === 0) return;

    // Flatten quarters with months for export
    const allDetails: Report[] = [];
    quarters.forEach((quarter) => {
      allDetails.push(quarter);
      if (quarter.months && quarter.months.length > 0) {
        allDetails.push(...quarter.months);
      }
    });
    allDetails.push(...yearData);

    exportSummaryToCSV(
      headings,
      allDetails,
      effectiveReportType,
      selectedReport?.description || 'report',
      selectedReport?.year || new Date().getFullYear()
    );
  };

  if (!fileId) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-dark-700 dark:bg-dark-800">
        <div className="py-12 text-center">
          <p className="text-gray-500 dark:text-gray-400">Please select a report to view</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* Error Alert */}
      {error && (
        <Alert variant="error" onClose={clearError}>
          {error}
        </Alert>
      )}
      {queryErrorMessage && <Alert variant="error">{queryErrorMessage}</Alert>}

      {/* Heading Sections - Angular Style with Purple Header */}
      {loading
        ? // Loading skeletons — compact card + KPI strip + detail grid
          headings.length === 0 && (
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-dark-600 dark:bg-dark-800">
              <div className="space-y-1 bg-gradient-to-r from-slate-700 via-[#4d5a85] to-slate-700 px-3 py-2.5 dark:from-dark-800 dark:via-slate-800 dark:to-dark-800 sm:px-4">
                <Skeleton className="h-2.5 w-16 bg-white/25" animation="pulse" />
                <Skeleton className="h-5 w-48 bg-white/30" animation="pulse" />
              </div>
              <div className="space-y-3 p-3 sm:p-4">
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="rounded-lg border border-primary-200/40 bg-primary-50/50 p-3 dark:border-primary-900/30 dark:bg-primary-950/20"
                    >
                      <Skeleton className="mb-2 h-2.5 w-20" animation="pulse" />
                      <Skeleton className="h-6 w-28" animation="pulse" />
                    </div>
                  ))}
                </div>
                <div>
                  <Skeleton className="mb-2 h-2.5 w-14" animation="pulse" />
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                    {Array.from({ length: effectiveReportType === 'monthly' ? 8 : 5 }).map(
                      (_, i) => (
                        <div
                          key={i}
                          className="rounded-md border border-gray-100 bg-gray-50/80 px-2.5 py-2 dark:border-dark-600 dark:bg-dark-900/30"
                        >
                          <Skeleton className="mb-1.5 h-2 w-full max-w-[5rem]" animation="pulse" />
                          <Skeleton className="h-4 w-16" animation="pulse" />
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          )
        : headings.map((heading, index) => {
            const sectionTitle = heading.isYTDValue
              ? `Year ${selectedReport?.year || new Date().getFullYear()} to Date`
              : heading.isFullYearValue
                ? `Year ${selectedReport?.year || new Date().getFullYear()}`
                : 'Summary';

            const visibleHeadings = getVisibleHeadings(
              effectiveReportType,
              heading.isYTDValue || false,
              heading.isFullYearValue || false,
              summaryMobileLayout
            );
            const { primary, secondary } = splitHeadingProperties(visibleHeadings);

            return (
              <div
                key={index}
                className="overflow-hidden rounded-xl border border-gray-200/90 bg-white shadow-md ring-1 ring-black/[0.03] dark:border-dark-600 dark:bg-dark-800 dark:ring-white/[0.04]"
              >
                <div className="relative border-b border-white/10 bg-gradient-to-r from-[#3a4568] via-[#4a5682] to-[#3a4568] px-3 py-2.5 dark:from-dark-800 dark:via-slate-800 dark:to-dark-800 sm:px-4 sm:py-3">
                  <div
                    className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_120%_at_100%_0%,rgba(255,255,255,0.12),transparent_55%)] dark:bg-[radial-gradient(ellipse_80%_120%_at_100%_0%,rgba(56,189,248,0.08),transparent_50%)]"
                    aria-hidden
                  />
                  <div className="relative">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/55">
                      {summarySectionBadge(heading)}
                    </p>
                    <h2 className="mt-0.5 text-base font-semibold leading-snug tracking-tight text-white sm:text-lg">
                      {sectionTitle}
                    </h2>
                  </div>
                </div>

                <div className="space-y-3 bg-gradient-to-b from-gray-50/80 to-white p-3 dark:from-dark-900/40 dark:to-dark-800 sm:space-y-3.5 sm:p-4">
                  {primary.length > 0 && (
                    <div>
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Key figures
                      </p>
                      <div
                        className={`grid gap-2 sm:gap-3 ${
                          primary.length === 1
                            ? 'grid-cols-1'
                            : primary.length === 2
                              ? 'grid-cols-2'
                              : 'grid-cols-2 lg:grid-cols-3'
                        }`}
                      >
                        {primary.map((prop: HeadingProperty) => (
                          <div
                            key={prop.key}
                            className="rounded-lg border border-primary-200/60 bg-gradient-to-br from-primary-50 via-white to-sky-50/40 p-2.5 shadow-sm dark:border-primary-800/50 dark:from-primary-950/40 dark:via-dark-900/80 dark:to-dark-900/60 sm:p-3"
                          >
                            <span className="block text-[10px] font-semibold uppercase leading-tight tracking-wide text-primary-800/85 dark:text-primary-200/80 sm:text-[11px]">
                              {prop.label}
                            </span>
                            <span className="mt-1 block text-base font-bold tabular-nums leading-tight text-gray-900 dark:text-white sm:text-lg">
                              {prop.format(heading)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {secondary.length > 0 && (
                    <div className="border-t border-gray-200/80 pt-3 dark:border-dark-600/80">
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Fleet &amp; pool details
                      </p>
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                        {secondary.map((prop: HeadingProperty) => (
                          <div
                            key={prop.key}
                            className="rounded-md border border-gray-200/90 bg-white/90 px-2.5 py-2 shadow-sm dark:border-dark-600 dark:bg-dark-900/50 sm:px-3 sm:py-2.5"
                          >
                            <span className="block text-[10px] font-medium leading-snug text-gray-500 dark:text-gray-400">
                              {prop.label}
                            </span>
                            <span className="mt-0.5 block text-sm font-semibold tabular-nums text-gray-900 dark:text-gray-100">
                              {prop.format(heading)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}

      {/* Data Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200/90 bg-white shadow-md ring-1 ring-black/[0.03] dark:border-dark-600 dark:bg-dark-800 dark:ring-white/[0.04]">
        <div className="flex flex-col gap-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white px-4 py-3 dark:border-dark-600 dark:from-dark-900/50 dark:to-dark-800 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-3.5">
          <div>
            <h2 className="type-section-title">Detailed Breakdown</h2>
            <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
              Quarters, months, and year totals
            </p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExportCSV}
            leftIcon={<Download className="h-4 w-4" />}
            disabled={loading || (headings.length === 0 && quarters.length === 0)}
            className="w-full shrink-0 rounded-full px-4 shadow-sm sm:w-auto"
          >
            Export to Excel
          </Button>
        </div>

        {loading ? (
          <div className="report-table-shell">
            {/* Loading skeleton matching table structure */}
            <table className="report-table">
              <thead className={poolReportTheadMutedClass}>
                <tr>
                  {Array.from({ length: effectiveReportType === 'monthly' ? 11 : 9 }).map(
                    (_, i) => (
                      <th key={i} className={poolReportThClass}>
                        <Skeleton className="ml-auto h-4 w-24" />
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i} className="border-b-2 border-gray-300 dark:border-dark-600">
                    {Array.from({ length: effectiveReportType === 'monthly' ? 11 : 9 }).map(
                      (_, j) => (
                        <td key={j} className="px-2.5 py-2 sm:px-3 sm:py-2.5">
                          <Skeleton className={j === 0 ? 'h-5 w-20' : 'ml-auto h-5 w-16'} />
                        </td>
                      )
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="report-table-shell">
            {/* Desktop Table View - Angular Style */}
            <table className="report-table">
              <thead className={poolReportTheadStickyClass}>
                <tr>
                  <th className={clsx(poolReportThLabelClass, 'min-w-[120px]')}>Period</th>
                  <th className={poolReportThClass}>Vessel Days</th>
                  <th className={poolReportThClass}>Weighted Days (Spot)</th>
                  {effectiveReportType === 'monthly' && (
                    <>
                      <th className={poolReportThClass}>Weighted Days (Unfixed)</th>
                      <th className={poolReportThClass}>Estimate (Spot)</th>
                    </>
                  )}
                  <th className={poolReportThClass}>100 Pts Fixed Rates (Spot)</th>
                  <th className={clsx(poolReportThClass, 'text-primary-800 dark:text-primary-200')}>
                    Total
                  </th>
                  <th className={poolReportThClass}>Contribution</th>
                  <th className={poolReportThClass}>Distribution</th>
                  <th className={poolReportThClass}>Admin, Bunker & Loan</th>
                  <th className={poolReportThClass}>Working Capital</th>
                </tr>
              </thead>
              <tbody>
                {quarters.map((quarter, qIndex) => {
                  const isExpanded = expandedQuarters.has(qIndex);
                  const months = quarter.months || [];
                  const hasMonths = months.length > 0;

                  return (
                    <React.Fragment key={qIndex}>
                      {/* Quarter Row */}
                      <tr
                        className={`border-b-2 border-gray-300 hover:bg-gray-50 dark:border-dark-600 dark:hover:bg-dark-700/30 ${hasMonths ? 'cursor-pointer' : ''}`}
                        onClick={hasMonths ? () => toggleQuarter(qIndex) : undefined}
                      >
                        <td className="px-2.5 py-2 text-left text-sm font-semibold text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          <div className="flex items-center gap-2">
                            {hasMonths && (
                              <ChevronDown
                                className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                              />
                            )}
                            {quarter.rowLabel}
                          </div>
                        </td>
                        <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {formatNumber(quarter.vesselDays)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {formatNumber(quarter.weightedDaysSpot)}
                        </td>
                        {effectiveReportType === 'monthly' && (
                          <>
                            <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(quarter.weightedDaysUnifixed)}
                            </td>
                            <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                              {formatNumber(quarter.estimatedSpot)}
                            </td>
                          </>
                        )}
                        <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {formatNumber(quarter.oneHundredPointsFixedRatesSpot)}
                        </td>
                        <td className="bg-primary-50/40 px-2.5 py-2 text-right text-sm font-bold tabular-nums text-primary-900 dark:bg-primary-950/25 dark:text-primary-100 sm:px-3 sm:py-2.5">
                          {formatNumber(quarter.total)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {formatNumber(quarter.contribution)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {formatNumber(quarter.distribution)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {formatNumber(quarter.adminAndBunker)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {formatNumber(quarter.workingCapital)}
                        </td>
                      </tr>

                      {/* Month Rows (collapsed/expanded) */}
                      {hasMonths &&
                        isExpanded &&
                        months.map((month, mIndex) => (
                          <tr
                            key={mIndex}
                            className="border-b border-gray-100 bg-gray-50/50 hover:bg-gray-100 dark:border-dark-700 dark:bg-dark-900/20 dark:hover:bg-dark-900/40"
                          >
                            <td className="px-2.5 py-1.5 pl-10 text-right text-sm text-gray-700 dark:text-gray-300 sm:px-3 sm:py-2 sm:pl-12">
                              {month.rowLabel}
                            </td>
                            <td className="px-2.5 py-1.5 text-right text-sm text-gray-700 dark:text-gray-300 sm:px-3 sm:py-2">
                              {formatNumber(month.vesselDays)}
                            </td>
                            <td className="px-2.5 py-1.5 text-right text-sm text-gray-700 dark:text-gray-300 sm:px-3 sm:py-2">
                              {formatNumber(month.weightedDaysSpot)}
                            </td>
                            {effectiveReportType === 'monthly' && (
                              <>
                                <td className="px-2.5 py-1.5 text-right text-sm text-gray-700 dark:text-gray-300 sm:px-3 sm:py-2">
                                  {formatNumber(month.weightedDaysUnifixed)}
                                </td>
                                <td className="px-2.5 py-1.5 text-right text-sm text-gray-700 dark:text-gray-300 sm:px-3 sm:py-2">
                                  {formatNumber(month.estimatedSpot)}
                                </td>
                              </>
                            )}
                            <td className="px-2.5 py-1.5 text-right text-sm text-gray-700 dark:text-gray-300 sm:px-3 sm:py-2">
                              {formatNumber(month.oneHundredPointsFixedRatesSpot)}
                            </td>
                            <td className="bg-primary-50/25 px-2.5 py-1.5 text-right text-sm font-semibold tabular-nums text-gray-900 dark:bg-primary-950/20 dark:text-gray-100 sm:px-3 sm:py-2">
                              {formatNumber(month.total)}
                            </td>
                            <td className="px-2.5 py-1.5 text-right text-sm text-gray-700 dark:text-gray-300 sm:px-3 sm:py-2">
                              {formatNumber(month.contribution)}
                            </td>
                            <td className="px-2.5 py-1.5 text-right text-sm text-gray-700 dark:text-gray-300 sm:px-3 sm:py-2">
                              {formatNumber(month.distribution)}
                            </td>
                            <td className="px-2.5 py-1.5 text-right text-sm text-gray-700 dark:text-gray-300 sm:px-3 sm:py-2">
                              {formatNumber(month.adminAndBunker)}
                            </td>
                            <td className="px-2.5 py-1.5 text-right text-sm text-gray-700 dark:text-gray-300 sm:px-3 sm:py-2">
                              {formatNumber(month.workingCapital)}
                            </td>
                          </tr>
                        ))}
                    </React.Fragment>
                  );
                })}

                {/* Year Total Rows */}
                {yearData.map((year, yIndex) => (
                  <tr
                    key={`year-${yIndex}`}
                    className="border-b-2 border-gray-300 bg-gradient-to-r from-gray-100 to-gray-50/95 font-semibold dark:border-dark-600 dark:from-dark-700/80 dark:to-dark-800/60"
                  >
                    <td className="border-l-[3px] border-primary-600 px-2.5 py-2 pl-2.5 text-left text-sm text-gray-900 dark:border-primary-500 dark:text-white sm:px-3 sm:py-2.5 sm:pl-3">
                      {year.rowLabel}
                    </td>
                    <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                      {formatNumber(year.vesselDays)}
                    </td>
                    <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                      {formatNumber(year.weightedDaysSpot)}
                    </td>
                    {effectiveReportType === 'monthly' && (
                      <>
                        <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {formatNumber(year.weightedDaysUnifixed)}
                        </td>
                        <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                          {formatNumber(year.estimatedSpot)}
                        </td>
                      </>
                    )}
                    <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                      {formatNumber(year.oneHundredPointsFixedRatesSpot)}
                    </td>
                    <td className="bg-primary-100/70 px-2.5 py-2 text-right text-sm font-bold tabular-nums text-primary-950 dark:bg-primary-950/40 dark:text-primary-50 sm:px-3 sm:py-2.5">
                      {formatNumber(year.total)}
                    </td>
                    <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                      {formatNumber(year.contribution)}
                    </td>
                    <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                      {formatNumber(year.distribution)}
                    </td>
                    <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                      {formatNumber(year.adminAndBunker)}
                    </td>
                    <td className="px-2.5 py-2 text-right text-sm text-gray-900 dark:text-white sm:px-3 sm:py-2.5">
                      {formatNumber(year.workingCapital)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
